import React, {useState} from "react";
import axios from "axios";
import {z} from "zod";
import {Eye, EyeOff, Info, KeyRound, ShieldCheck} from "lucide-react";
import {getAuthToken, getClientIdOfUser, getLoggedInUserId, showToast} from "../../utils/utility";
import * as Constants from "../../utils/Constants";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import {cn} from "../../utils/cn";

const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@#$])[A-Za-z\d@#$]{8,}$/;

const ChangePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, {message: "Old password is required"}),
    newPassword: z.string()
      .min(8, {message: "New password must be at least 8 characters"})
      .regex(passwordRegex, {
        message:
          "New password must contain letters, numbers and at least one symbol from @ # $. No other special characters allowed",
      }),
    confirmNewPassword: z.string().min(1, {message: "Confirm password is required"}),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "New password and confirm password do not match",
    path: ["confirmNewPassword"],
  })
  .refine((data) => data.oldPassword !== data.newPassword, {
    message: "Old password and new password should not be the same",
    path: ["newPassword"],
  });

const PasswordField = ({label, value, onChange, show, onToggle, error, autoFocus}) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-ink-700">
      {label} <span className="text-danger-600">*</span>
    </label>
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        autoFocus={autoFocus}
        autoComplete="new-password"
        className={cn(
          "block w-full h-9 pl-3 pr-10 text-base rounded-md",
          "bg-white border shadow-inset",
          "text-ink-900 placeholder:text-ink-400",
          "hover:border-ink-300 focus:outline-none focus:ring",
          error
            ? "border-danger-500 focus:border-danger-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.25)]"
            : "border-ink-200 focus:border-brand-500"
        )}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded text-ink-400 hover:text-ink-600 hover:bg-ink-100"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
    {error && <p className="text-xs text-danger-600">{error}</p>}
  </div>
);

const ChangePassword = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const clearForm = () => {
    setOldPassword(""); setNewPassword(""); setConfirmNewPassword(""); setErrors({});
  };

  const handleSubmit = async () => {
    setErrors({});
    const parsed = ChangePasswordSchema.safeParse({oldPassword, newPassword, confirmNewPassword});
    if (!parsed.success) {
      const zodErrors = parsed.error.flatten();
      const fieldErrors = {};
      if (zodErrors.fieldErrors) {
        Object.entries(zodErrors.fieldErrors).forEach(([key, arr]) => {
          if (arr && arr.length > 0) fieldErrors[key] = arr[0];
        });
      }
      setErrors(fieldErrors);
      showToast(Object.values(fieldErrors)[0] ?? "Validation error", {type: "error"});
      return;
    }
    setLoading(true);
    const endpoint = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/users/changePassword/${getLoggedInUserId()}`;
    try {
      const response = await axios.put(
        endpoint,
        {oldPassword, newPassword, confirmNewPassword},
        {headers: {Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json"}}
      );
      const {responseCode, responseMessage} = response.data || {};
      if (responseCode === "200" || responseCode === 200) {
        showToast(responseMessage || "Password updated successfully", {type: "success"});
        clearForm();
      } else {
        showToast(responseMessage || "Error changing password", {type: "error"});
      }
    } catch (error) {
      const resp = error?.response?.data;
      showToast(resp?.responseMessage || "Network error", {type: "error"});
    } finally {
      setLoading(false);
    }
  };

  const ruleMet = (test) => (
    <span className={cn("inline-flex items-center gap-1.5 text-xs", test ? "text-success-700" : "text-ink-500")}>
      <ShieldCheck className={cn("h-3.5 w-3.5", test ? "text-success-600" : "text-ink-300")} />
      {test ? "Looks good" : ""}
    </span>
  );

  return (
    <div className="max-w-[720px] mx-auto pb-8">
      <Card>
        <div className="p-6 border-b border-ink-100">
          <h2 className="text-base font-semibold text-ink-900 flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-brand-700" />
            Change password
          </h2>
          <p className="text-sm text-ink-500 mt-1">
            Pick a strong password. You'll need to sign in again on other devices after this change.
          </p>
        </div>

        <div className="p-6 space-y-5">
          <PasswordField
            label="Old password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            show={showOld}
            onToggle={() => setShowOld(!showOld)}
            error={errors.oldPassword}
            autoFocus
          />
          <PasswordField
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            show={showNew}
            onToggle={() => setShowNew(!showNew)}
            error={errors.newPassword}
          />
          <PasswordField
            label="Confirm new password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            show={showConfirm}
            onToggle={() => setShowConfirm(!showConfirm)}
            error={errors.confirmNewPassword}
          />

          <div className="rounded-lg bg-info-50 border border-info-100 px-4 py-3 text-sm text-info-700 flex items-start gap-2">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              Minimum 8 characters. Must contain letters, numbers, and at least one symbol from
              <span className="font-mono mx-1 bg-white/60 px-1 rounded">@ # $</span>.
              Other special characters are not allowed.
            </div>
          </div>
          {newPassword && ruleMet(passwordRegex.test(newPassword))}
        </div>

        <div className="px-6 py-4 border-t border-ink-100 flex justify-end gap-2">
          <Button variant="secondary" type="button" onClick={clearForm} disabled={loading}>Reset</Button>
          <Button variant="primary" type="button" onClick={handleSubmit} loading={loading}>
            Change password
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ChangePassword;
